import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle } from 'lucide-react';
import api from '@/lib/api';

export function NeedsAttentionCard({ navigate }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get('/dashboard/needs-attention')
      .then(r => setData(r.data))
      .catch(() => setData({ stale_projects: [], stale_leads: [] }));
  }, []);

  const projects = data?.stale_projects || [];
  const leads = data?.stale_leads || [];
  if (!data || (projects.length === 0 && leads.length === 0)) return null;

  return (
    <Card className="border-amber-500/25 bg-card shadow-none" data-testid="needs-attention-card">
      <CardHeader className="pb-2 border-b border-white/[0.08]">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <AlertTriangle className="h-3.5 w-3.5 text-amber-400 shrink-0" /> Needs Attention
          <span className="text-xs text-muted-foreground/70 ml-auto tabular-nums">{projects.length + leads.length}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-2 space-y-1">
        {projects.length > 0 && (
          <div data-testid="needs-attention-projects">
            <p className="text-[10px] uppercase tracking-[0.08em] font-semibold text-amber-400 pt-1.5">
              No time logged in {data.project_days}+ days
            </p>
            {projects.map(p => (
              <button
                key={p.project_id}
                type="button"
                className="w-full text-left flex items-center justify-between py-2 border-b border-white/[0.06] last:border-0 gap-2 hover:bg-secondary/20 transition-colors rounded-sm"
                onClick={() => navigate(`/projects/${p.project_id}`)}
                data-testid={`stale-project-${p.project_id}`}
              >
                <p className="text-sm font-normal truncate flex-1">{p.project_label}</p>
                <span className="text-xs text-amber-400 font-mono tabular-nums shrink-0">{p.days_since}d</span>
              </button>
            ))}
          </div>
        )}
        {leads.length > 0 && (
          <div data-testid="needs-attention-leads">
            <p className="text-[10px] uppercase tracking-[0.08em] font-semibold text-amber-400 pt-1.5">
              Leads with no activity in {data.lead_days}+ days
            </p>
            {leads.map(l => (
              <button
                key={l.id}
                type="button"
                className="w-full text-left flex items-center justify-between py-2 border-b border-white/[0.06] last:border-0 gap-2 hover:bg-secondary/20 transition-colors rounded-sm"
                onClick={() => navigate(`/leads?lead=${l.id}`)}
                data-testid={`stale-lead-${l.id}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-normal truncate">{l.company || l.contact_name}</p>
                  {l.company && l.contact_name && <p className="text-xs text-muted-foreground truncate">{l.contact_name}</p>}
                </div>
                <Badge variant="outline" className="text-xs shrink-0">{l.pipeline_stage}</Badge>
                <span className="text-xs text-amber-400 font-mono tabular-nums shrink-0">{l.days_since}d</span>
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
